import { useClickOutside } from 'stimulus-use'
import flatpickr from 'flatpickr'
import ApplicationController from './application_controller'
import moment from 'moment'

import I18n from '@app/javascript/i18n/index.js.erb'

export default class extends ApplicationController {
  static targets = [
    'selectMenu',
    'selectedOptionInput',
    'rangePickerContainer',
    'rangePicker',
    'startDate',
    'endDate',
    'option'
  ]

  doConnect() {
    useClickOutside(this, { onlyVisible: true })
    this.initializeFlatpicker()
    this.preselectOption()
  }

  clickOutside(event) {
    this.classToggler?.removeAll(event)
  }

  get classToggler() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'class-toggler')
  }

  preselectOption() {
    const startDate = this.selectedOptionInputTarget.getAttribute('data-option-start-date')
    const endDate = this.selectedOptionInputTarget.getAttribute('data-option-end-date')

    if (startDate && endDate) {
      const selectedOptionId = this.selectedOptionInputTarget.value

      const selectedOption = this.optionWithId(selectedOptionId)
      if (this.isOptionCustomRange(selectedOption)) {
        this.rangePickerContainerTarget.classList.remove('hidden')
        this.fp.setDate([startDate, endDate], true)
      }
      this.changeDateValues([startDate, endDate])
    }
  }

  isOptionCustomRange(optionDOM) {
    return optionDOM.hasAttribute(`data-${this.identifier}-param-option-custom-range`)
  }

  optionWithId(id) {
    return this.optionTargets.find((option) => { return option.getAttribute(`data-${this.identifier}-id-param`) === id })
  }

  selectOption(event) {
    const option = this.getTargetIn(event.currentTarget, 'option')

    this.changeSelectedOption(option)

    if (this.isOptionCustomRange(option)) {
      this.showDatePickerSelector()
    } else {
      const startDate = this.getParam(option, 'option-start-date')
      const endDate = this.getParam(option, 'option-end-date')

      if (startDate && endDate) {
        this.hideDatePickerSelector()
        this.changeDateValues([startDate, endDate])
      }
    }
  }

  clearSelection(event) {
    this.startDateTarget.value = ''
    this.endDateTarget.value = ''
    this.rangePickerTarget.value = ''
    this.selectedOptionInputTarget.value = ''
    this.selectedOptionInputTarget.removeAttribute('data-option-start-date')
    this.selectedOptionInputTarget.removeAttribute('data-option-end-date')
    this.hideDatePickerSelector()
    this.fp.clear()
  }

  initializeFlatpicker() {
    const that = this
    const startDate = this.startDateTarget.value
    const endDate = this.endDateTarget.value

    let defaultDate
    if (startDate && endDate) {
      defaultDate = [new Date(startDate), new Date(endDate)]
    }

    this.fp = flatpickr(this.rangePickerContainerTarget, {
      mode: 'range',
      defaultDate: defaultDate,
      onChange: function(selectedDates, _dateStr, _instance) {
        if (selectedDates.length === 2) {
          that.rangePickerTarget.value = that.formattedDateRange(...selectedDates)

          that.changeDateValues([
            moment(selectedDates[0]).format(moment.HTML5_FMT.DATE),
            moment(selectedDates[1]).format(moment.HTML5_FMT.DATE)
          ])
        }
      },
      onClose: function(selectedDates, _dateStr, _instance) {
        if (selectedDates.length === 1) {
          that.fp.clear()
        }
      }
    })

    if (startDate && endDate) {
      that.rangePickerTarget.value = this.formattedDateRange(startDate, endDate)
      this.rangePickerContainerTarget.classList.remove('hidden')
    } else {
      this.startDateTarget.value = ''
      this.endDateTarget.value = ''
      this.rangePickerTarget.value = ''
      this.hideDatePickerSelector()
      this.fp.clear()
    }
  }

  formattedDateRange(startDate, endDate) {
    startDate = moment(startDate).format(I18n.t('date.formats.default_js'))
    endDate = moment(endDate).format(I18n.t('date.formats.default_js'))

    return I18n.t('components.date_range_selector.picker_value', { start_date: startDate, end_date: endDate })
  }

  changeSelectedOption(option) {
    this.selectedOptionInputTarget.value = option.parentElement.getAttribute('data-node-selector-param-node-id')
  }

  // this method is triggered after each hiddenfield value is changed
  changeDateValues([startDate, endDate]) {
    if (startDate && endDate) {
      this.startDateTarget.value = startDate
      this.endDateTarget.value = endDate

      // Trigger the change event manually
      // ie compatibility
      let event
      if (typeof (Event) === 'function') {
        event = new Event('change')
      } else {
        event = document.createEvent('Event')
        event.initEvent('change', true, true)
      }
      // end ie compatibility
      this.startDateTarget.dispatchEvent(event)
      this.endDateTarget.dispatchEvent(event)
    }
  }

  // shows the date picker selector.
  showDatePickerSelector() {
    this.rangePickerContainerTarget.classList.remove('hidden')
    this.fp.open()
    this.fp.clear()
  }

  hideDatePickerSelector() {
    this.rangePickerContainerTarget.classList.add('hidden')
  }
}
